import {
  useCallback,
  type DetailsHTMLAttributes,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import {
  elementKey,
  useAnnotationElementStateStore,
} from "~/components/build/Show/lib/useAnnotationElementStateStore";

interface Props extends Omit<DetailsHTMLAttributes<HTMLDetailsElement>, "open" | "onToggle"> {
  annotationId: string;
  /** Stable path within the body, e.g. `details[2]`. Generated by `AnnotationBody`. */
  path: string;
  /**
   * Initial open state inferred from the server-rendered HTML (`<details open>` ↔ true).
   * Used until the user toggles the element, after which the store wins.
   */
  initialOpen: boolean;
  children: ReactNode;
}

/**
 * Controlled `<details>` whose `open` state lives in `useAnnotationElementStateStore` rather
 * than on the DOM node itself. This means the open state survives:
 *
 *  - React unmount/remount (e.g. when an annotations list is virtualised)
 *  - React re-renders triggered by refetched annotation HTML
 *  - websocket-driven `annotations:changed` invalidations
 */
export function AnnotationDetails({ annotationId, path, initialOpen, children, ...rest }: Props) {
  const key = elementKey(annotationId, path);

  const open = useAnnotationElementStateStore((state) => state.detailsOpen[key] ?? initialOpen);
  const setDetailsOpen = useAnnotationElementStateStore((state) => state.setDetailsOpen);

  const handleToggle = useCallback(
    (event: SyntheticEvent<HTMLDetailsElement>) => {
      setDetailsOpen(key, event.currentTarget.open);
    },
    [key, setDetailsOpen],
  );

  return (
    <details {...rest} open={open} onToggle={handleToggle}>
      {children}
    </details>
  );
}
